import { useState } from "react";
import { toast } from "sonner";
import { Link, useParams } from "react-router-dom";
import { ShoppingBag, Phone, Truck, ShieldCheck } from "lucide-react";
import { useFetch } from "@/hooks/useFetch";
import { useCart } from "@/context/CartContext";
import { Seo } from "@/components/site/Seo";
import { Breadcrumbs } from "@/components/site/PageHero";
import { Img, RuneMark } from "@/components/site/Img";
import { Markdown } from "@/components/site/Markdown";
import { ProductCard, StockBadge } from "@/components/site/ProductCard";
import { ContactForm } from "@/components/forms/ContactForm";
import { money, PHONE, PHONE_HREF } from "@/lib/api";
import NotFound from "./NotFound";

function Gallery({ images, name }) {
  const [i, setI] = useState(0);
  if (!images?.length)
    return <div className="knot-bg flex aspect-square items-center justify-center border border-[color:var(--line)] bg-[#0f1216]"><RuneMark className="h-16 w-16 text-[color:var(--line-2)]" /></div>;
  const cur = images[Math.min(i, images.length - 1)];
  return (
    <div data-testid="product-gallery">
      <div className="relative aspect-square overflow-hidden border border-[color:var(--line)] bg-[#0f1216]">
        <Img key={cur.url} src={cur.url} alt={cur.alt || name} eager sizes="(max-width:1024px) 100vw, 50vw" className="rise h-full w-full object-contain" data-testid="product-gallery-main" />
      </div>
      {images.length > 1 && (
        <div className="mt-3 grid grid-cols-5 gap-2 sm:grid-cols-6">
          {images.map((im, k) => (
            <button key={im.url} onClick={() => setI(k)} aria-label={`View image ${k + 1}`} data-testid={`product-gallery-thumb-${k}`}
              className={`aspect-square overflow-hidden border bg-[#0f1216] transition-colors ${k === i ? "border-[color:var(--ice)]" : "border-[color:var(--line)] hover:border-slate-500"}`}>
              <Img src={im.url} alt="" sizes="96px" className="h-full w-full object-cover" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

const STOCK = { in_stock: "In stock", low_stock: "Low stock", out_of_stock: "Out of stock", on_order: "On order", enquire: "Enquire for availability" };

function ProductDetails({ p, site }) {
  const rows = [
    ["Manufacturer", p.manufacturer || p.brand], ["Master / Slave", p.tool_type && (p.tool_type === "master" ? "Master" : "Slave")],
    ["Linked master", p.linked_master], ["Programming methods", (p.programming_methods || []).join(" / ")],
    ["Vehicle / licence group", p.vehicle_group], ["What's included", p.whats_included], ["Updates / subscription", p.update_position],
    ["Stock status", STOCK[p.stock_status]], ["Delivery", p.delivery_info || site?.dispatch_text], ["Support", p.support_info || site?.support_text],
    ["Important licence notes", p.licence_notes],
  ].filter(([, v]) => v);
  return (
    <section className="mt-16" data-testid="product-details">
      <h2 className="mb-6 font-display text-3xl font-bold uppercase">Product details</h2>
      <dl className="divide-y divide-[color:var(--line)] border-y border-[color:var(--line)]">
        {rows.map(([k, v]) => (
          <div key={k} className="grid gap-2 py-4 sm:grid-cols-[240px_1fr]" data-testid={`product-detail-${k.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
            <dt className="font-mono text-[11px] uppercase tracking-[0.16em] text-slate-500">{k}</dt>
            <dd className="whitespace-pre-line text-sm text-slate-200">{v}</dd>
          </div>
        ))}
      </dl>
    </section>
  );
}

export default function ProductPage() {
  const { slug } = useParams();
  const { data, error, loading } = useFetch(`/products/${slug}`);
  const { add } = useCart();
  const [qty, setQty] = useState(1);
  const [sel, setSel] = useState({});
  const { data: site } = useFetch("/site");
  if (error) return <NotFound />;
  if (loading || !data) return <div className="container-x py-24"><div className="h-[60vh] animate-pulse bg-[color:var(--bg-2)]" /></div>;
  const { product: p, categories, related } = data;
  const onSale = p.compare_at_price && p.compare_at_price > p.price;
  const cat = categories.find((c) => c.id === p.primary_category_id) || categories.find((c) => c.parent_id) || categories[0];
  const opts = p.options || [];
  const delta = opts.reduce((s, o) => s + (o.values.find((v) => v.label === sel[o.name])?.price_delta || 0), 0);
  const allChosen = opts.every((o) => sel[o.name]);
  const vat = p.vat_mode === "default" ? site?.default_vat_mode || "incl" : p.vat_mode;
  const vatLabel = { incl: "inc. VAT", excl: "+ VAT", exempt: "VAT exempt" }[vat];
  return (
    <div data-testid="product-page">
      <Seo path={`/product-page/${p.slug}`} />
      <section className="container-x pb-16 pt-10">
        <Breadcrumbs items={[["/", "Home"], ["/shop", "Shop"], ...(cat ? [[`/shop/category/${cat.slug}`, cat.name]] : []), [null, p.name]]} />
        <div className="grid gap-12 lg:grid-cols-2">
          <Gallery images={p.images} name={p.name} />
          <div className="lg:pt-4">
            {p.brand && <p className="eyebrow">{p.brand}</p>}
            <h1 className="mt-3 font-display text-4xl font-bold uppercase leading-[0.95] sm:text-5xl" data-testid="page-h1">{p.name}</h1>
            <p className="mt-5 text-slate-300">{p.short_description}</p>
            <div className="mt-8 flex items-end gap-4">
              <span className="font-mono text-4xl text-[color:var(--ice)]" data-testid="product-price">{money(p.price + delta)}</span>
              <span className="pb-1.5 font-mono text-xs text-slate-500" data-testid="product-vat-label">{vatLabel}</span>
              {onSale && <span className="pb-1 font-mono text-lg text-slate-500 line-through" data-testid="product-compare-price">{money(p.compare_at_price)}</span>}
            </div>
            <div className="mt-3"><StockBadge status={p.stock_status} testId="product-stock-status" /></div>
            {opts.map((o) => (
              <div key={o.name} className="mt-6" data-testid={`product-option-${o.name}`}>
                <p className="label">{o.name}</p>
                <div className="flex flex-wrap gap-2">
                  {o.values.map((v) => {
                    const out = v.stock_qty !== null && v.stock_qty !== undefined && v.stock_qty <= 0 && !p.allow_backorder;
                    return (
                      <button key={v.label} disabled={out} onClick={() => setSel({ ...sel, [o.name]: v.label })} data-testid={`product-option-value-${v.label}`}
                        className={`border px-4 py-2 text-sm transition-colors disabled:opacity-40 ${sel[o.name] === v.label ? "border-[color:var(--ice)] bg-[color:var(--ice)]/10 text-[color:var(--ice)]" : "border-[color:var(--line-2)] text-slate-300 hover:border-slate-400"}`}>
                        {v.label}{v.price_delta ? ` (+${money(v.price_delta)})` : ""}{out ? " — out of stock" : ""}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
            {p.purchasable ? (
              <div className="mt-8 flex flex-wrap gap-3">
                <div className="flex items-center border border-[color:var(--line-2)]">
                  <button onClick={() => setQty(Math.max(1, qty - 1))} className="px-4 py-3 text-slate-300" aria-label="Decrease quantity" data-testid="product-qty-dec">−</button>
                  <span className="w-10 text-center font-mono" data-testid="product-qty">{qty}</span>
                  <button onClick={() => setQty(Math.min(20, qty + 1))} className="px-4 py-3 text-slate-300" aria-label="Increase quantity" data-testid="product-qty-inc">+</button>
                </div>
                <button onClick={() => allChosen ? add(p, qty, sel, p.price + delta) : toast.error("Please choose " + opts.filter((o) => !sel[o.name]).map((o) => o.name).join(", "))} className="btn-ice flex-1 sm:flex-none" data-testid="add-to-cart-button"><ShoppingBag className="h-4 w-4" />Add to basket</button>
              </div>
            ) : (
              <a href="#enquire" className="btn-ice mt-8" data-testid="product-enquire-button">Enquire about this item</a>
            )}
            <div className="mt-8 grid gap-3 border-t border-[color:var(--line)] pt-6 text-sm text-slate-400 sm:grid-cols-2">
              <p className="flex items-center gap-3"><Truck className="h-4 w-4 text-[color:var(--bronze)]" />{p.stock_status === "out_of_stock" && p.allow_backorder ? "Available on backorder" : p.delivery_info || site?.dispatch_text || "Free next-working-day UK delivery"}</p>
              <p className="flex items-center gap-3"><ShieldCheck className="h-4 w-4 text-[color:var(--bronze)]" />Genuine tools, set up before dispatch</p>
              <a href={PHONE_HREF} className="flex items-center gap-3 hover:text-white"><Phone className="h-4 w-4 text-[color:var(--bronze)]" />Package prices: {PHONE}</a>
            </div>
            {categories.length > 0 && (
              <div className="mt-6 flex flex-wrap gap-2">
                {categories.map((c) => <Link key={c.id} to={`/shop/category/${c.slug}`} className="border border-[color:var(--line)] px-3 py-1 font-mono text-[10px] uppercase tracking-[0.16em] text-slate-400 hover:border-[color:var(--ice)] hover:text-[color:var(--ice)]">{c.name}</Link>)}
              </div>
            )}
          </div>
        </div>
      </section>
      <section className="container-x grid gap-14 border-t border-[color:var(--line)] py-16 lg:grid-cols-[1.3fr_1fr]">
        <div>
          <h2 className="mb-6 font-display text-3xl font-bold uppercase">Description</h2>
          <Markdown testId="product-description">{p.description}</Markdown>
        </div>
        <ProductDetails p={p} site={site} />
        {p.specs?.length > 0 && (
          <div>
            <h2 className="mb-6 font-display text-3xl font-bold uppercase">Specifications</h2>
            <dl className="divide-y divide-[color:var(--line)] border-y border-[color:var(--line)]" data-testid="product-specs">
              {p.specs.map((s) => (
                <div key={s.label} className="grid grid-cols-[130px_1fr] gap-4 py-3.5">
                  <dt className="font-mono text-[11px] uppercase tracking-[0.16em] text-slate-500">{s.label}</dt>
                  <dd className="text-sm text-slate-200">{s.value}</dd>
                </div>
              ))}
            </dl>
          </div>
        )}
      </section>
      <section id="enquire" className="container-x scroll-mt-28 border-t border-[color:var(--line)] py-16">
        <div className="grid gap-10 lg:grid-cols-[1fr_1.4fr]">
          <div><h2 className="font-display text-3xl font-bold uppercase">Questions about this tool?</h2><p className="mt-3 text-slate-400">Compatibility, package pricing or trade-in — ask and we'll reply directly.</p></div>
          <ContactForm product={p.name} defaultSubject={`Enquiry: ${p.name}`} />
        </div>
      </section>
      {related.length > 0 && (
        <section className="container-x border-t border-[color:var(--line)] py-16" data-testid="related-products">
          <h2 className="mb-8 font-display text-3xl font-bold uppercase">Related products</h2>
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">{related.map((r) => <ProductCard key={r.id} p={r} />)}</div>
        </section>
      )}
    </div>
  );
}
